// Leaderboards modal — 3 tabs (level / crystals / win %). Mock top-10
// data below a real YOU row pinned at the top of each tab, computed
// from the persistent wallet state.

import { getState, getWinRate } from '../meta/wallet';

type LeaderboardTab = 'level' | 'crystals' | 'winrate';

type LeaderboardEntry = {
  rank: number | 'you';
  name: string;
  role: 'int' | 'ext';
  value: string;
  isYou?: boolean;
};

const NAMES = [
  'shadowfang', 'pixelwave',  'neonpanda',  'ghostbyte',  'luminari',
  'vantablack', 'retroqueen', 'starburst',  'frostbyte',  'emberline',
];
const ROLES: ('int' | 'ext')[] = ['int', 'ext', 'ext', 'int', 'ext', 'int', 'int', 'ext', 'int', 'ext'];

function mockData(tab: LeaderboardTab): LeaderboardEntry[] {
  return NAMES.map((n, i) => {
    const value = tab === 'level'
      ? `Lv ${50 - i * 2}`
      : tab === 'crystals'
      ? `${(25000 - i * 2300).toLocaleString()} ✨`
      : `${(87 - i * 3).toFixed(1)}%`;
    return { rank: i + 1, name: n, role: ROLES[i], value };
  });
}

// Build a YOU row from the wallet's current state for the active tab.
// Always pinned at the top regardless of actual rank vs the mock list.
function youRow(tab: LeaderboardTab): LeaderboardEntry {
  const s = getState();
  const value = tab === 'level'
    ? `Lv ${s.bestLevel}`
    : tab === 'crystals'
    ? `${s.crystals.toLocaleString()} ✨`
    : s.matchesPlayed > 0
    ? `${(getWinRate() * 100).toFixed(1)}%`
    : '—';
  // Role is unknown at the lobby level — use a generic neutral color.
  return { rank: 'you', name: 'YOU', role: 'int', value, isYou: true };
}

export function openLeaderboards(onClose?: () => void): () => void {
  let currentTab: LeaderboardTab = 'level';

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(2,6,23,.93);
    z-index: 9998;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: #e5e7eb; font: 700 14px system-ui, sans-serif;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 28px system-ui; letter-spacing: 4px; margin-bottom: 6px;
    background: linear-gradient(90deg,#fbbf24,#fde047,#f59e0b);
    -webkit-background-clip: text; background-clip: text; color: transparent;
  `;
  title.textContent = '🏆 LEADERBOARDS';
  overlay.appendChild(title);

  const sub = document.createElement('div');
  sub.style.cssText = `opacity:.65;font-size:11px;letter-spacing:2.5px;margin-bottom:18px;`;
  sub.textContent = 'TOP 10 · WEEKLY';
  overlay.appendChild(sub);

  const tabs = document.createElement('div');
  tabs.style.cssText = `
    display: flex; gap: 4px; margin-bottom: 14px;
    background: rgba(15,23,42,.85);
    padding: 4px;
    border-radius: 10px;
    border: 1px solid #334155;
  `;
  const tabDefs: { id: LeaderboardTab; label: string }[] = [
    { id: 'level',    label: 'LEVEL'    },
    { id: 'crystals', label: 'CRYSTALS' },
    { id: 'winrate',  label: 'WIN %'    },
  ];
  const tabButtons: HTMLButtonElement[] = [];
  for (const t of tabDefs) {
    const btn = document.createElement('button');
    btn.textContent = t.label;
    btn.dataset.tab = t.id;
    btn.style.cssText = `
      padding: 8px 24px;
      border: 0;
      border-radius: 7px;
      font: 800 12px system-ui;
      letter-spacing: 1.5px;
      cursor: pointer;
      background: transparent;
      color: #94a3b8;
      transition: background .1s, color .1s;
    `;
    btn.addEventListener('click', () => {
      currentTab = t.id;
      renderList();
    });
    tabs.appendChild(btn);
    tabButtons.push(btn);
  }
  overlay.appendChild(tabs);

  const list = document.createElement('div');
  list.style.cssText = `
    width: 460px;
    max-width: 92vw;
    background: rgba(15,23,42,.92);
    border: 1px solid #475569;
    border-radius: 12px;
    padding: 10px 0;
    max-height: 50vh;
    overflow-y: auto;
    box-shadow: 0 16px 40px rgba(0,0,0,.6);
  `;
  overlay.appendChild(list);

  function renderList() {
    for (const btn of tabButtons) {
      const active = btn.dataset.tab === currentTab;
      btn.style.background = active ? 'rgba(56,189,248,.22)' : 'transparent';
      btn.style.color = active ? '#fde047' : '#94a3b8';
    }
    const you = youRow(currentTab);
    const entries: LeaderboardEntry[] = [you, ...mockData(currentTab)];
    list.innerHTML = entries.map((e) => {
      const roleColor = e.isYou ? '#fde047' : e.role === 'int' ? '#60a5fa' : '#fb923c';
      const roleIcon = e.isYou ? '⭐' : e.role === 'int' ? '🥷' : '🎉';
      const medal = e.rank === 'you'
        ? 'YOU'
        : e.rank === 1
        ? '🥇'
        : e.rank === 2
        ? '🥈'
        : e.rank === 3
        ? '🥉'
        : `#${e.rank}`;
      const rowBg = e.isYou
        ? 'background: linear-gradient(90deg, rgba(253,224,71,.15), rgba(253,224,71,.02));'
        : '';
      const borderBottom = e.isYou
        ? 'border-bottom: 1px solid rgba(253,224,71,.45);'
        : 'border-bottom: 1px solid rgba(255,255,255,.05);';
      return `
        <div style="
          display: flex; align-items: center; gap: 14px;
          padding: 9px 22px;
          ${rowBg}
          ${borderBottom}
        ">
          <div style="width:42px;text-align:center;font:800 ${e.isYou ? '11' : '14'}px system-ui;letter-spacing:${e.isYou ? '1.5px' : '0'};color:${e.isYou ? '#fde047' : 'inherit'};">${medal}</div>
          <div style="width:22px;text-align:center;font-size:16px;">${roleIcon}</div>
          <div style="flex:1;color:${roleColor};font:${e.isYou ? '900' : '700'} 13px system-ui;letter-spacing:${e.isYou ? '1.5px' : '0'};">${e.name}</div>
          <div style="font:800 13px system-ui;color:#fde047;">${e.value}</div>
        </div>
      `;
    }).join('');
  }
  renderList();

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'CLOSE [ESC]';
  closeBtn.style.cssText = `
    margin-top: 18px;
    padding: 8px 22px;
    border-radius: 8px;
    border: 1px solid #475569;
    background: rgba(15,23,42,.85);
    color: #e5e7eb;
    cursor: pointer;
    font: 700 12px system-ui;
    letter-spacing: 1.5px;
  `;
  overlay.appendChild(closeBtn);

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', onKey);
    overlay.remove();
    onClose?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') close();
  };
  addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', close);

  document.body.appendChild(overlay);
  return close;
}
